/**
 * Centralized Role Utilities
 *
 * Edit the ADMIN_ROLES array below to control which roles
 * get admin-level access throughout the entire application.
 *
 * Admin access is granted if EITHER condition is true:
 *   1. The user's role.name is in ADMIN_ROLES, OR
 *   2. The user has isSudoAdmin === true from the auth API
 */

// ─── Roles that have full admin access ───────────────────────
// Add or remove role names here to grant/revoke admin privileges app-wide.
export const ADMIN_ROLES: readonly string[] = ['super_admin', 'admin'];

/**
 * Check if a role name has admin-level access.
 * For checks that only have access to the role string (e.g. inside useSelector callbacks).
 */
export function hasAdminAccess(roleName: string | undefined | null): boolean {
  if (!roleName) return false;
  return ADMIN_ROLES.includes(roleName);
}

/**
 * Check if a user has admin-level access using both role name AND isSudoAdmin flag.
 * Use this when you have access to the full userDetails object.
 *
 * @param userDetails - The full userDetails object from Redux (state.rootReducers.userData.userDetails)
 */
export function hasFullAdminAccess(userDetails: any): boolean {
  if (!userDetails) return false;
  // isSudoAdmin flag from auth API grants full admin access
  if (userDetails.isSudoAdmin === true) return true;
  // Otherwise check role name
  const roleName = userDetails?.role?.name;
  return hasAdminAccess(roleName);
}

/**
 * Check if a role is specifically super_admin.
 * Use only when you truly need to distinguish super_admin from admin.
 */
export function isSuperAdmin(roleName: string | undefined | null): boolean {
  return roleName === 'super_admin';
}
